import { Accessibility, ChevronLeft, Dog, PenLine, Plus, Search, X } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { sileo } from "sileo";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useGuests } from "@/hooks/use-guests";
import { useWaitlistCreate } from "@/hooks/use-waitlist";
import { isValidPhone, isoForDialCode, splitDialCode, trimDialCodeOnly } from "@/lib/phone";
import { readErrorDetail } from "@/lib/reservation-api";
import { useAppStore } from "@/store/store";
import type { ApiTableFeature } from "@/types/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function isValidEmail(email: string): boolean {
  return EMAIL_RE.test(email);
}

// Mirrors ``Sidebar.FeatureToggleChip``. Duplicated inline to avoid a
// circular import (Sidebar already imports this view). The chip is small
// enough that keeping two copies is cheaper than extracting a shared file.
function FeatureToggleChip({
  selected,
  onClick,
  label,
  icon,
}: {
  selected: boolean;
  onClick: () => void;
  label: string;
  icon: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={selected ? `Quitar ${label.toLowerCase()}` : `Agregar ${label.toLowerCase()}`}
      aria-pressed={selected}
      className={
        "inline-flex h-5 items-center justify-center rounded text-text-secondary transition-colors " +
        (selected
          ? "w-5 border border-[#c8c8c2] bg-[#f7f6f3] hover:bg-[#ececea]"
          : "gap-0.5 border border-dashed border-text-secondary/60 bg-white px-1 hover:bg-[#f7f6f3]")
      }
    >
      {!selected && <Plus className="size-2.5" aria-hidden="true" />}
      {icon}
    </button>
  );
}

// ── Main component ────────────────────────────────────────────────
//
// In-sidebar surface. Visual language mirrors ``NewReservationView``:
// mono uppercase section labels, framed Cliente card with dial-code +
// national-digits phone split, framed Espera block, dashed-border notes.

type NewWaitlistViewProps = {
  onClose: () => void;
};

export default function NewWaitlistView({ onClose }: NewWaitlistViewProps) {
  const newWaitlistOpen = useAppStore((s) => s.newWaitlistOpen);

  const create = useWaitlistCreate();

  // ── Local form state ─────────────────────────────────────────
  const [name, setName] = useState("");
  const [pax, setPax] = useState(2);
  const [phone, setPhone] = useState("");
  const [dialCode, setDialCode] = useState("+57");
  const [nationalDigits, setNationalDigits] = useState("");
  const [email, setEmail] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [features, setFeatures] = useState<ApiTableFeature[]>([]);

  // Existing CRM Guest the operator picked from the search dropdown.
  // ``null`` ⇒ free-form mode (the legacy behaviour: the backend will
  // resolve / create a Guest from the phone snapshot on submit).
  const [guestId, setGuestId] = useState<string | null>(null);
  // Search field state; debounced into the actual query so we don't
  // hammer the CRM endpoint on every keystroke. Mirrors Sidebar's
  // Nueva Reserva search.
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(searchQuery), 250);
    return () => clearTimeout(id);
  }, [searchQuery]);
  const shouldSearch = guestId === null && debouncedQuery.trim().length >= 2;
  const { data: searchResults } = useGuests(shouldSearch ? debouncedQuery : undefined);
  const visibleResults = (searchResults ?? []).slice(0, 6);

  // Reset whenever the surface mounts / re-opens.
  useEffect(() => {
    if (newWaitlistOpen) {
      setName("");
      setPax(2);
      setPhone("");
      setDialCode("+57");
      setNationalDigits("");
      setEmail("");
      setInternalNotes("");
      setFeatures([]);
      setGuestId(null);
      setSearchQuery("");
      setDebouncedQuery("");
    }
  }, [newWaitlistOpen]);

  // Keep the combined E.164 ``phone`` in sync with the split inputs.
  function writePhone(code: string, national: string) {
    const next = national ? `${code}${national}` : "";
    setPhone(next);
  }

  // ── Validation ───────────────────────────────────────────────
  const phoneIsValid = isValidPhone(phone);
  const emailIsValid = email.trim() === "" || isValidEmail(email.trim());
  const canSubmit =
    name.trim() !== "" && phoneIsValid && trimDialCodeOnly(phone) !== "" && emailIsValid;

  // ── Handlers ─────────────────────────────────────────────────
  function toggleFeature(feature: ApiTableFeature) {
    setFeatures((prev) =>
      prev.includes(feature) ? prev.filter((f) => f !== feature) : [...prev, feature],
    );
  }

  function selectGuest(g: {
    id: string;
    name: string;
    phone?: string | null;
    email?: string | null;
  }) {
    // Snapshot the CRM contact into the form so the operator can still
    // override before submit. ``guestId`` flips the create payload into
    // the "link to existing Guest" path on the backend.
    setGuestId(g.id);
    setName(g.name);
    setEmail(g.email ?? "");
    setPhone(g.phone ?? "");
    const { code, national } = splitDialCode(g.phone ?? "");
    setDialCode(code || "+57");
    setNationalDigits(national);
    setSearchQuery("");
    setDebouncedQuery("");
  }

  function clearGuest() {
    setGuestId(null);
    setName("");
    setPhone("");
    setDialCode("+57");
    setNationalDigits("");
    setEmail("");
    setSearchQuery("");
    setDebouncedQuery("");
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit || create.isPending) return;

    create.mutate(
      {
        guest_name: name.trim(),
        pax,
        ...(guestId ? { guest_id: guestId } : {}),
        guest_phone: phone,
        guest_email: email.trim(),
        features,
        internal_notes: internalNotes.trim(),
      },
      {
        onSuccess: () => {
          sileo.success({
            title: "Agregado a la lista",
            description: `${name.trim()} · ${pax} pax`,
          });
          onClose();
        },
        onError: (err) =>
          sileo.error({
            title: "No se pudo agregar",
            description: readErrorDetail(err) ?? "Intenta de nuevo.",
          }),
      },
    );
  }

  // Sync the split fields whenever an external paste lands on ``phone``.
  useEffect(() => {
    const { code, national } = splitDialCode(phone);
    setDialCode(code || "+57");
    setNationalDigits(national);
  }, [phone]);

  // ── Render sections ──────────────────────────────────────────
  // Header mirrors ``NewReservationView``: ghost "Volver" on the left,
  // section pill on the right.
  const header = (
    <div className="flex items-center gap-2 border-b border-border bg-surface px-5 py-3">
      <Button
        variant="ghost"
        size="sm"
        onClick={onClose}
        className="-ml-2 inline-flex h-8 items-center gap-1 px-2 text-text-secondary hover:text-text-primary"
      >
        <ChevronLeft className="size-4" />
        Volver
      </Button>
      <span className="ml-auto text-[12px] font-semibold text-text-primary">
        Nueva entrada · lista de espera
      </span>
    </div>
  );

  const body = (
    <form
      id="walkin-waitlist-form"
      onSubmit={handleSubmit}
      className="flex flex-1 flex-col gap-4 overflow-y-auto px-5 py-4"
    >
      {/* Cliente block — same visual language as NewReservationView's
          Cliente block: search by name/phone to attach an existing CRM
          Guest, or type a fresh name to create one inline. Picking from
          the dropdown snapshots the Guest's contact into the editable
          form below so the operator can still tweak it before saving. */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <span className="font-mono text-[10px] font-bold uppercase tracking-wider text-text-secondary">
            Cliente
          </span>
          {guestId && (
            <button
              type="button"
              onClick={clearGuest}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-text-secondary hover:text-text-primary"
            >
              <X className="size-3" />
              Cambiar cliente
            </button>
          )}
        </div>

        {/* Search input only when no guest is picked. A guest pick swaps
            this for the editable name row below. */}
        {!guestId && (
          <div className="relative">
            <div className="flex items-center gap-1.5 rounded-lg border border-[#c8c8c2] bg-white px-2.5 py-1.5">
              <Search className="size-3.5 shrink-0 text-text-secondary" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar cliente por nombre o teléfono…"
                aria-label="Buscar cliente"
                className="min-w-0 flex-1 bg-transparent text-[13px] text-text-primary outline-none"
              />
            </div>
            {shouldSearch && visibleResults.length > 0 && (
              <div className="absolute left-0 right-0 top-full z-30 mt-1 max-h-52 overflow-y-auto rounded-lg border border-[#c8c8c2] bg-white shadow-lg">
                {visibleResults.map((g) => (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() =>
                      selectGuest({
                        id: g.id,
                        name: g.name,
                        phone: g.phone ?? undefined,
                        email: g.email ?? undefined,
                      })
                    }
                    className="flex w-full items-center justify-between gap-2 border-b border-[#ececea] px-3 py-2 text-left text-[13px] last:border-b-0 hover:bg-[#f7f6f3]"
                  >
                    <span className="font-medium text-text-primary">{g.name}</span>
                    <span className="text-[11.5px] text-text-secondary">{g.phone || g.email}</span>
                  </button>
                ))}
              </div>
            )}
            {shouldSearch && visibleResults.length === 0 && (
              <p className="mt-1 text-[11px] italic text-text-secondary">
                Sin coincidencias — completa el nombre abajo para agregar uno nuevo.
              </p>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 text-[18px] font-extrabold leading-tight tracking-tight">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nombre del cliente"
            aria-label="Nombre del cliente"
            className="min-w-0 flex-1 border-b border-dashed border-text-secondary/60 bg-transparent pb-0.5 text-[18px] font-extrabold leading-tight tracking-tight outline-none focus:border-text-primary"
          />
          <span className="inline-flex items-center gap-1.5">
            <FeatureToggleChip
              selected={features.includes("pet_friendly")}
              onClick={() => toggleFeature("pet_friendly")}
              label="Mascota"
              icon={<Dog className="size-3" />}
            />
            <FeatureToggleChip
              selected={features.includes("accessible")}
              onClick={() => toggleFeature("accessible")}
              label="Accesibilidad"
              icon={<Accessibility className="size-3" />}
            />
          </span>
        </div>

        <div className="overflow-hidden rounded-lg border border-[#c8c8c2] bg-white">
          {/* Phone row — dial-code box + national-digits input, same split
              NewReservationView uses so the operator learns one paradigm. */}
          <div className="grid grid-cols-[22px_1fr_auto] items-center gap-1.5 border-b border-[#ececea] px-2.5 py-1.5 text-[13px]">
            <span className="text-center font-mono text-[12px] text-text-secondary">☎</span>
            <div className="flex items-center gap-1">
              <div className="flex items-stretch rounded border border-dashed border-text-secondary/60 bg-white focus-within:border-solid focus-within:border-text-primary">
                <span className="flex items-center border-r border-dashed border-text-secondary/40 px-1 font-mono text-[10px] uppercase tracking-wider text-text-secondary">
                  {isoForDialCode(dialCode) || "—"}
                </span>
                <span className="flex items-center pl-1.5 font-mono text-[12px] text-text-primary">
                  +
                </span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={dialCode.startsWith("+") ? dialCode.slice(1) : dialCode}
                  onChange={(e) => {
                    const digits = e.target.value.replaceAll(/\D/g, "").slice(0, 4);
                    const next = digits ? `+${digits}` : "+";
                    setDialCode(next);
                    writePhone(next, nationalDigits);
                  }}
                  aria-label="Indicativo del país"
                  className="w-10 bg-transparent py-0.5 pr-1.5 font-mono text-[12px] text-text-primary outline-none"
                />
              </div>
              <input
                type="tel"
                inputMode="tel"
                value={nationalDigits}
                onChange={(e) => {
                  const digits = e.target.value.replaceAll(/\D/g, "");
                  setNationalDigits(digits);
                  writePhone(dialCode, digits);
                }}
                placeholder="300 000 0000"
                aria-invalid={!phoneIsValid}
                aria-label="Número de teléfono"
                className={
                  "min-w-0 flex-1 rounded border bg-white px-1.5 py-0.5 font-mono text-[12px] text-text-primary outline-none focus:border-solid " +
                  (phoneIsValid
                    ? "border-dashed border-text-secondary/60 focus:border-text-primary"
                    : "border-solid border-red-500 focus:border-red-600")
                }
              />
            </div>
            <PenLine className="size-3 text-text-secondary/70" />
          </div>
          <div className="grid grid-cols-[22px_1fr_auto] items-center gap-1.5 px-2.5 py-1.5 text-[13px]">
            <span className="text-center font-mono text-[12px] text-text-secondary">✉</span>
            <input
              type="email"
              inputMode="email"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="nombre@correo.com"
              aria-invalid={!emailIsValid}
              aria-label="Email del cliente"
              className={
                "rounded border bg-white px-1.5 py-0.5 text-[12.5px] text-text-primary outline-none focus:border-solid " +
                (emailIsValid
                  ? "border-dashed border-text-secondary/60 focus:border-text-primary"
                  : "border-solid border-red-500 focus:border-red-600")
              }
            />
            <PenLine className="size-3 text-text-secondary/70" />
          </div>
        </div>
      </div>

      {/* Espera block — Personas stepper + Zona preferida, both inside a
          framed card matching the Reserva block in NewReservationView. */}
      <div className="flex flex-col gap-1.5">
        <div className="font-mono text-[10px] font-bold uppercase tracking-wider text-text-secondary">
          Espera
        </div>
        <div className="overflow-hidden rounded-[10px] border border-[#c8c8c2] bg-[#f7f6f3]">
          <div className="flex flex-col gap-2.5 p-3.5">
            {/* Personas — same stepper visuals as the locked Personas cell
                in NewReservationView. */}
            <div className="rounded-lg border border-[#c8c8c2] bg-white px-3 py-2">
              <div className="mb-0.5 flex items-center gap-1 font-mono text-[9px] font-bold uppercase tracking-widest text-text-secondary">
                Personas
                <PenLine className="size-2.5" />
              </div>
              <div className="flex items-center justify-between gap-1">
                <button
                  type="button"
                  onClick={() => setPax((p) => Math.max(1, p - 1))}
                  className="flex h-7 w-7 items-center justify-center rounded border border-[#c8c8c2] bg-white text-[16px] font-bold text-text-primary hover:bg-[#f7f6f3] disabled:opacity-40"
                  aria-label="Quitar una persona"
                >
                  −
                </button>
                <span className="font-mono text-[22px] font-extrabold leading-none text-text-primary">
                  {pax}
                  <span className="ml-0.5 text-[11px] font-medium tracking-wider text-text-secondary">
                    pax
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => setPax((p) => Math.min(20, p + 1))}
                  className="flex h-7 w-7 items-center justify-center rounded border border-[#c8c8c2] bg-white text-[16px] font-bold text-text-primary hover:bg-[#f7f6f3]"
                  aria-label="Agregar una persona"
                >
                  +
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Notas internas — same dashed-border textarea style as the
          "Notas del cliente" field in NewReservationView. */}
      <div className="flex flex-col gap-1.5">
        <div className="font-mono text-[10px] font-bold uppercase tracking-wider text-text-secondary">
          Notas internas
        </div>
        <Textarea
          rows={2}
          value={internalNotes}
          onChange={(e) => setInternalNotes(e.target.value)}
          placeholder="Anotaciones para el equipo (alergias, ocasiones, etc.)"
          className="resize-none rounded-lg border-dashed bg-white text-[13px] text-text-primary"
        />
      </div>
    </form>
  );

  // Footer mirrors ``NewReservationView``: outline Cancelar on the left,
  // primary submit on the right. Submit keeps the green CTA accent to
  // distinguish the waitlist flow visually.
  const footer = (
    <div className="flex items-center justify-between gap-2 border-t border-border bg-surface px-5 py-3">
      <Button variant="outline" onClick={onClose} disabled={create.isPending}>
        Cancelar
      </Button>
      <Button
        type="submit"
        form="walkin-waitlist-form"
        disabled={!canSubmit || create.isPending}
        className="bg-green-600 text-white hover:bg-green-700"
      >
        {create.isPending ? "Agregando…" : "Agregar a lista de espera"}
      </Button>
    </div>
  );

  return (
    <div className="flex h-full flex-col">
      {header}
      {body}
      {footer}
    </div>
  );
}
